<template>
  <div class="text-start">
    <!--training module title-->
    <v-row no-gutters>
      <v-col>
        <LocalisedContentEditable
          :values="trainingModule.title"
          @update:values="updateTrainingModuleTitle"
          :prefix="`${$t('course.courseDetails.module')} ${
            selectedTrainingModule.trainingModuleIndex + 1
          }:`"
          :placeholder="$t('course.courseDetails.moduleNameHere')"
          :editable="isEditing"
          css-class="text-h6 font-weight-bold textLight--text"
          :hasError="trainingModule.hasInvalidTitle"
          :selectedLocale="selectedLocale"
        />
      </v-col>
    </v-row>

    <!--training module contents count-->
    <v-row no-gutters class="mb-3">
      <v-col>
        <span
          class="body-1 font-weight-light primary--text"
          style="opacity: 0.5"
        >
          {{ getTrainingModuleContentsCountText(trainingModule) }}
        </span>
      </v-col>
    </v-row>

    <!--training module description-->
    <v-row no-gutters class="mb-6">
      <v-col>
        <LocalisedContentEditable
          :values="trainingModule.description"
          @update:values="updateTrainingModuleDescription"
          :text-area-rows="5"
          :placeholder="$t('course.courseDetails.moduleDescriptionHere')"
          :editable="isEditing"
          css-class="textLight--text body-1 font-weight-light"
          :selectedLocale="selectedLocale"
        />
      </v-col>
    </v-row>

    <!--training module contents-->
    <v-row
      v-for="(
        trainingModuleContent, trainingModuleContentIndex
      ) in trainingModule.trainingModuleContents"
      :key="`trainingModuleContent_${trainingModuleContentIndex}`"
      no-gutters
      class="mb-3"
    >
      <v-col>
        <v-card
          flat
          class="pa-3 rounded-lg"
          :class="
            trainingModuleContent.hasInvalidTitle ||
            hasInvalidQuiz(trainingModuleContent)
              ? 'error-border'
              : ''
          "
          @click="onTrainingModuleContentSelection(trainingModuleContentIndex)"
        >
          <v-row justify="center" align="center">
            <!--training module content icon & title-->
            <v-col>
              <!--training module content icon-->
              <v-menu :disabled="!isEditing">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" color="textLight">
                    {{
                      getTrainingModuleContentTypeIcon(trainingModuleContent)
                    }}
                  </v-icon>
                </template>

                <v-list>
                  <v-list-item
                    v-for="(contentType, index) in contentTypes"
                    :key="index"
                  >
                    <v-list-item-title
                      class="text-capitalize hand-cursor"
                      @click="
                        updateTrainingModuleContentType({
                          trainingModuleContentIndex,
                          contentType,
                        })
                      "
                    >
                      {{ contentType.toLowerCase() }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!--training module content title-->
              <span class="textLight--text body-2">
                {{
                  getTrainingModuleContentTitle(
                    trainingModuleContent,
                    selectedLocale
                  )
                }}
              </span>
            </v-col>

            <!--training module content duration-->
            <v-col cols="12" sm="auto" class="text-end pt-0 pt-sm-3 ps-sm-0">
              <span
                class="textLight--text body-2 justify-center align-center"
                style="opacity: 0.2"
              >
                {{
                  `${trainingModuleContent.duration || 0} ${$t(
                    "course.courseDetails.minutes"
                  )}`
                }}
              </span>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!--training module content add button-->
    <v-row v-if="isEditing" no-gutters>
      <v-col>
        <CourseContentAddButton
          :contentTypes="contentTypes"
          :disabled="trainingModule.hasInvalidTitle"
          @onClick="addTrainingModuleContent"
        />
      </v-col>
    </v-row>

    <!--training module save button-->
    <v-row v-if="isEditing" no-gutters justify="end" class="mt-6 mb-6">
      <v-col cols="auto">
        <v-btn
          small
          depressed
          color="#D75B5B"
          min-width="80"
          class="rounded-1 ms-2 white--text"
          :loading="isTrainingModuleDeleting"
          :disabled="isTrainingModuleSaving"
          @click="deleteTrainingModule"
        >
          <v-icon small class="me-1">mdi-delete</v-icon>

          {{ $t("course.courseDetails.deleteModule") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import LocalisedContentEditable from "@/app/core-module/components/LocalisedContentEditable";
import CourseContentAddButton from "@/app/course-module/components/course-details/CourseContentAddButton";

export default {
  name: "CourseModule",
  components: { LocalisedContentEditable, CourseContentAddButton },
  props: {
    training: {
      type: Object,
      required: true,
    },
    selectedTrainingModule: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    selectedLocale: {
      type: String,
      default: "en",
    },
  },
  data() {
    return {
      contentTypes: ["VIDEO", "TEXT", "AUDIO", "QUIZ"],
      isTrainingModuleSaving: false,
      isTrainingModuleDeleting: false,
    };
  },
  computed: {
    trainingModule() {
      return this.training.trainingModules[
        this.selectedTrainingModule.trainingModuleIndex
      ];
    },
  },
  methods: {
    hasInvalidQuiz(trainingModuleContent) {
      return (
        trainingModuleContent.contentType === "QUIZ" &&
        trainingModuleContent.quiz?.questionAnswers?.some(
          qa => qa.hasInvalidQuestion || qa.hasInvalidChoices
        )
      );
    },
    emitUpdateTraining(updateObject) {
      const clonedTraining = { ...this.training };

      if (updateObject === "DELETE") {
        clonedTraining.deletedTrainingModules = [
          ...(clonedTraining.deletedTrainingModules ?? []),
          clonedTraining.trainingModules[
            this.selectedTrainingModule.trainingModuleIndex
          ]?._id,
        ];

        clonedTraining.trainingModules.splice(
          this.selectedTrainingModule.trainingModuleIndex,
          1
        );
      } else {
        clonedTraining.trainingModules[
          this.selectedTrainingModule.trainingModuleIndex
        ] = {
          ...this.trainingModule,
          ...updateObject,
        };
      }

      this.$emit("update:training", clonedTraining);
    },
    updateTrainingModuleTitle(values) {
      this.emitUpdateTraining({
        title: values,
        hasChangesInTitle: true,
        hasInvalidTitle: !values?.find(x => x.value?.trim()?.length),
      });
    },
    updateTrainingModuleDescription(values) {
      this.emitUpdateTraining({
        description: values,
        hasChangesInDescription: true,
      });
    },
    deleteTrainingModule() {
      if (this.isTrainingModuleDeleting) return;

      this.$dialog
        .confirm(this.$t("course.courseDetails.confirmTrainingModuleDelete"))
        .then(() => {
          this.emitUpdateTraining("DELETE");
        })
        .catch(_ => {});
    },
    onTrainingModuleContentSelection(trainingModuleContentIndex) {
      this.$emit(
        "onTrainingModuleContentSelection",
        trainingModuleContentIndex
      );
    },
    updateTrainingModuleContentType({
      trainingModuleContentIndex,
      contentType,
    }) {
      this.trainingModule.trainingModuleContents[trainingModuleContentIndex] = {
        ...this.trainingModule.trainingModuleContents[
          trainingModuleContentIndex
        ],
        contentType: contentType,
        hasChangesInContentType: true,
      };

      this.emitUpdateTraining();
    },
    addTrainingModuleContent(contentType) {
      const trainingModuleContent = {
        training: {
          _id: this.training._id,
        },
        trainingModule: {
          _id: this.trainingModule._id,
        },
        contentType,
        hasChangesInContentType: true,
        hasInvalidTitle: contentType !== "QUIZ",
      };

      this.trainingModule.trainingModuleContents = [
        ...(this.trainingModule.trainingModuleContents ?? []),
        trainingModuleContent,
      ];

      this.onTrainingModuleContentSelection(
        this.trainingModule.trainingModuleContents.length - 1
      );

      this.emitUpdateTraining();
    },
  },
};
</script>

<style scoped></style>
